import type { FlowValues, Money } from '@felix/domain';
import { formatMoney } from '../materials/inventory.js';
import type { VictuallingData, VictuallingFleetData } from './types.js';

/**
 * Aylık iaşe raporunun saf yardımcıları (ADR 0123): ay listesi, para birimi başına tutar metni ve
 * CSV dökümleri. Para birimleri hiçbir yerde toplanmaz.
 */

/** Akışın sütun sırası (açılış + giriş − tüketim − zayi ± sayım = kapanış). */
export const FLOW_COLUMNS = [
  'opening',
  'receipts',
  'consumption',
  'disposal',
  'adjustments',
  'closing',
] as const satisfies readonly (keyof FlowValues)[];

/** Seçilebilir aylar: bugünün ayından geriye `count` ay (yeniden eskiye). */
export function victuallingMonths(today: string, count = 12): string[] {
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7)) - 1;
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(Date.UTC(year, month - i, 1));
    return d.toISOString().slice(0, 7);
  });
}

/** Önceki ay (`YYYY-MM`). */
export function previousMonth(month: string): string {
  const d = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 2, 1));
  return d.toISOString().slice(0, 7);
}

/** Tutar listesinden bir para biriminin tutarı; yoksa `null`. */
export function amountIn(money: Money, currency: string): number | null {
  return money.find((m) => m.currency === currency)?.amount ?? null;
}

/** Para birimi başına tutar metni ("1.234,00 USD · 12,00 EUR"); boşsa tire. */
export function moneyText(money: Money, locale: string): string {
  return money.length === 0
    ? '—'
    : money.map((m) => formatMoney(m.amount, m.currency, locale)).join(' · ');
}

/** İşaretli yüzde ("+15,0 %", "−3,2 %"); `null` tire. */
export function signedPercent(value: number | null, locale: string): string {
  if (value === null) return '—';
  const text = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Math.abs(value));
  return value > 0 ? `+${text} %` : value < 0 ? `−${text} %` : `${text} %`;
}

function csvCell(value: string | number | null): string {
  const text = value === null ? '' : String(value);
  return /[",\n;]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

const csv = (rows: readonly (readonly (string | number | null)[])[]) =>
  rows.map((row) => row.map(csvCell).join(',')).join('\n');

const FLOW_TITLES: Record<(typeof FLOW_COLUMNS)[number], string> = {
  opening: 'Opening',
  receipts: 'Receipts',
  consumption: 'Consumption',
  disposal: 'Write-off',
  adjustments: 'Count Difference',
  closing: 'Closing',
};

/**
 * Geminin raporunun CSV'si (Excel açar): kalem başına miktar akışı (kalemin biriminde) ve her
 * para birimi için değer akışı; ondalık noktalı.
 */
export function victuallingCsv(report: VictuallingData): string {
  const head = [
    'Vessel',
    'Month',
    'Category Code',
    'Category',
    'Item Code',
    'Item',
    'Cost Center',
    'In Victualling Rate',
    'Unit',
    ...FLOW_COLUMNS.map((c) => `${FLOW_TITLES[c]} Qty`),
    ...report.currencies.flatMap((currency) =>
      FLOW_COLUMNS.map((c) => `${FLOW_TITLES[c]} (${currency})`),
    ),
    'Unpriced',
  ];
  const rows = report.items.map((item) => [
    report.vessel.name,
    report.month,
    item.categoryCode,
    item.mainCategory,
    item.itemCode,
    item.name,
    item.costCenter,
    item.inRateBasis ? 'Yes' : 'No',
    item.unit,
    ...FLOW_COLUMNS.map((c) => item.quantity[c]),
    ...report.currencies.flatMap((currency) =>
      FLOW_COLUMNS.map((c) => amountIn(item.value[c], currency) ?? 0),
    ),
    item.unpriced ? 'Yes' : 'No',
  ]);
  return csv([head, ...rows]);
}

/** Filo görünümünün CSV'si: gemi × para birimi satırları, sonda para birimi başına filo. */
export function fleetCsv(fleet: VictuallingFleetData): string {
  const head = [
    'Vessel',
    'Month',
    'Person-days',
    'Average POB',
    'Currency',
    'Crew Consumption',
    'Victualling Rate',
    'Budget Rate',
    'Budget Amount',
    'Variance',
    'Variance %',
    'Unpriced Items',
  ];
  const rows: (string | number | null)[][] = [];
  for (const vessel of fleet.rows) {
    const rates = vessel.rates.length > 0 ? vessel.rates : [null];
    for (const rate of rates) {
      rows.push([
        vessel.name,
        fleet.month,
        vessel.personDays,
        vessel.averagePob,
        rate?.currency ?? null,
        rate?.consumption ?? null,
        rate?.rate ?? null,
        rate?.budget?.dailyRate ?? null,
        rate?.budget?.amount ?? null,
        rate?.budget?.variance ?? null,
        rate?.budget?.variancePct ?? null,
        vessel.unpricedItems,
      ]);
    }
  }
  for (const total of fleet.totals) {
    rows.push([
      'Fleet',
      fleet.month,
      total.personDays,
      null,
      total.currency,
      total.consumption,
      total.rate,
      null,
      null,
      null,
      null,
      null,
    ]);
  }
  return csv([head, ...rows]);
}
